import { useEffect, useMemo, useState } from "react";
import { Field, Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import classNames from "classnames";
import Cookies from "js-cookie";
import { format, isValid, parse } from "date-fns";
import style from "../EventsAudit/EventAuditList.module.css";

import { useAppSelector } from "../../shared/hook";
import CalendarSelectFormik from "../../shared/Ui/Calendar/CalendarSelectFormik";
import SelectCustomFormik from "../../shared/Ui/SelectCustom/SelectCustomFormik";
import { getAllOrganisations } from "../../common/api/authApi";
import { normalizedLocale } from "../../common/libs/normalizedLocale";
import { useProductGroupOptions } from "../../shared/hook/useProductGroupOptions";
import { createBalanceReport } from "../../common/api/billingApi";

type SelectOption = { value: string; label: string };
type OrgOption = SelectOption & { iinBin: string };

type FormValues = {
  dateFrom: string;
  dateTo: string;
  orgIinBin: string;
  productGroupCode: string;
};

const toApiDate = (value: string) => {
  const parsed = parse(value, "dd.MM.yyyy", new Date());
  if (!isValid(parsed)) return "";
  return format(parsed, "yyyy-MM-dd");
};

function BalanceReportCreatePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const userData: any = useAppSelector((state) => state.auth.userData);
  const locale = normalizedLocale(i18n.language);

  const isOperator = userData?.selectRoleV2?.some(
    (role: { roleId: number }) => role.roleId === 5,
  );

  const [formError, setFormError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Товарные группы с названием на языке интерфейса (значение — код ТГ)
  const { optionsByCode: productGroupOptions } = useProductGroupOptions();
  const [allOrgs, setAllOrgs] = useState<OrgOption[]>([]);
  const [orgQuery, setOrgQuery] = useState("");
  const [selectedOrg, setSelectedOrg] = useState<SelectOption | null>(null);

  useEffect(() => {
    if (!isOperator) return;
    getAllOrganisations()
      .then((response: any) => {
        const list = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response?.content)
              ? response.content
              : [];
        const options = list
          .map((org: any) => {
            const iinBin = String(org?.iinBin || org?.bin || org?.iin || "").trim();
            if (!iinBin) return null;
            const name = String(
              locale === "kk"
                ? org?.nameKk || org?.nameRu || org?.name || ""
                : org?.nameRu || org?.nameKk || org?.name || "",
            )
              .replace(/\s+/g, " ")
              .trim();
            const label = [iinBin, name].filter(Boolean).join(" ");
            return { value: iinBin, label, iinBin };
          })
          .filter(Boolean) as OrgOption[];
        setAllOrgs(options);
      })
      .catch(() => setAllOrgs([]));
  }, [isOperator, locale]);

  const orgSelectOptions = useMemo(() => {
    const q = orgQuery.trim().toLowerCase();
    const options =
      q.length < 3 ? [] : allOrgs.filter((org) => org.iinBin.includes(q));
    return selectedOrg && !options.some((o) => o.value === selectedOrg.value)
      ? [selectedOrg, ...options]
      : options;
  }, [allOrgs, orgQuery, selectedOrg]);

  const orgNoOptionsMessage = ({ inputValue }: { inputValue: string }) =>
    (inputValue || "").trim().length < 3
      ? t("marking_codes_report.organization_hint")
      : t("marking_codes_report.organization_empty");

  const validatePeriod = (dateFrom: string, dateTo: string) => {
    const from = parse(dateFrom, "dd.MM.yyyy", new Date());
    const to = parse(dateTo, "dd.MM.yyyy", new Date());
    if (!isValid(from) || !isValid(to)) {
      return t("billing.requiredField");
    }
    if (from > to) {
      return t("billing.periodInvalidRange");
    }
    return "";
  };

  const handleGenerate = async (values: FormValues) => {
    setFormError("");
    const periodError = validatePeriod(values.dateFrom, values.dateTo);
    if (periodError) {
      setFormError(periodError);
      return;
    }

    const orgIinBin =
      isOperator && values.orgIinBin.trim()
        ? values.orgIinBin.trim()
        : undefined;

    setIsSubmitting(true);
    try {
      const productGroupCode = isOperator
        ? values.productGroupCode || undefined
        : Cookies.get("activeGroupCode") || undefined;

      const response = await createBalanceReport({
        dateFrom: toApiDate(values.dateFrom),
        dateTo: toApiDate(values.dateTo),
        orgIinBins: orgIinBin ? [orgIinBin] : [],
        productGroupCodes: productGroupCode ? [productGroupCode] : undefined,
      });
      if (!response?.id) {
        setFormError(t("marking_codes_report.create_error"));
        return;
      }
      // replace: «Назад» со страницы отчёта ведёт в журнал, а не обратно в форму
      navigate(`/billing/balance-report/${response.id}`, { replace: true });
    } catch (error: any) {
      console.error("Error generating balance report:", error);
      setFormError(
        error?.response?.data?.message ||
          error?.response?.data?.errorMessage ||
          t("marking_codes_report.create_error"),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-1 flex-col min-w-0">
      <h2 className="my-3 text-xl font-semibold">
        {t("billing.balanceReportTitle")}
      </h2>

      <div className="bg-white rounded-xl shadow-md p-6 mb-5">
        <h3 className="text-lg font-semibold mb-4">
          {t("billing.generationParams")}
        </h3>
        <Formik
          initialValues={{
            dateFrom: "",
            dateTo: "",
            orgIinBin: "",
            productGroupCode: "",
          }}
          onSubmit={handleGenerate}
        >
          {({ setFieldValue }) => (
            <Form>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
                <div className={style.inputField}>
                  <div className={style.labelAndButtonWrapper}>
                    <label className={style.inputLabel}>
                      {t("billing.periodStart")}
                    </label>
                  </div>
                  <CalendarSelectFormik
                    name="dateFrom"
                    documentStatus="new"
                    outputFormat="dd.MM.yyyy"
                    showMonthYearSelect
                    placeholder={t("components.choose")}
                  />
                </div>
                <div className={style.inputField}>
                  <div className={style.labelAndButtonWrapper}>
                    <label className={style.inputLabel}>
                      {t("billing.periodEnd")}
                    </label>
                  </div>
                  <CalendarSelectFormik
                    name="dateTo"
                    documentStatus="new"
                    outputFormat="dd.MM.yyyy"
                    showMonthYearSelect
                    placeholder={t("components.choose")}
                  />
                </div>
              </div>

              {isOperator && (
                <div className={classNames(style.inputField, "mt-4 max-w-3xl")}>
                  <div className={style.labelAndButtonWrapper}>
                    <label className={style.inputLabel}>
                      {t("billing.productGroup")}
                    </label>
                  </div>
                  <Field
                    component={SelectCustomFormik}
                    name="productGroupCode"
                    isMulti={false}
                    isClearable
                    size="dm"
                    options={productGroupOptions}
                    placeholder={t("billing.allProductGroups")}
                    onChange={(next: string) =>
                      setFieldValue("productGroupCode", next || "")
                    }
                  />
                </div>
              )}

              {isOperator && (
                <div className={classNames(style.inputField, "mt-4 max-w-3xl")}>
                  <div className={style.labelAndButtonWrapper}>
                    <label className={style.inputLabel}>
                      {t("billing.orgBinShort")}
                    </label>
                  </div>
                  <Field
                    component={SelectCustomFormik}
                    name="orgIinBin"
                    isClearable
                    isMulti={false}
                    size="dm"
                    options={orgSelectOptions}
                    placeholder={t("billing.orgBinPlaceholder")}
                    onInputChange={(value: string) => setOrgQuery(value || "")}
                    noOptionsMessage={orgNoOptionsMessage}
                    onChange={(next: string) => {
                      setFieldValue("orgIinBin", next || "");
                      setSelectedOrg(
                        orgSelectOptions.find((o) => o.value === next) ?? null,
                      );
                    }}
                  />
                </div>
              )}

              {formError && (
                <p className="text-sm text-red-600 mt-3">{formError}</p>
              )}

              <div className="flex flex-row gap-[10px] justify-start mt-5">
                <button
                  type="submit"
                  className="bg-green text-white font-semibold py-2 px-4 rounded disabled:opacity-60"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? `${t("billing.reportStatusGenerating")}...`
                    : t("billing.generateReport")}
                </button>
                <button
                  type="button"
                  className="bg-bg text-gray-500 font-semibold py-2 px-4 rounded"
                  onClick={() => navigate("/billing/balance-report")}
                >
                  {t("billing.close")}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
}

export default BalanceReportCreatePage;
